import { useState, useEffect, useCallback, useRef } from 'react';

export interface DailyTask {
  id: string;
  title: string;
  description?: string;
  type: 'meditation' | 'content' | 'activity' | 'session' | 'mood' | 'social' | 'general' | 'breathing' | 'yoga' | 'journal' | 'health';
  timeCategory: 'sabah' | 'ogle' | 'aksam' | 'zamansiz';
  priority: 'high' | 'medium' | 'low';
  deeplink?: string;
  contentTitle?: string; // Actual content title from vector search
  contentType?: string; // Content type name (Meditation, Story, Talk, etc.)
  completed: boolean;
}

export interface WeeklyRoutines {
  [date: string]: DailyTask[];
}

export interface CategorizedTasks {
  sabah: DailyTask[];
  ogle: DailyTask[];
  aksam: DailyTask[];
  zamansiz: DailyTask[];
  all: DailyTask[];
}

export interface UseDailyRoutinesOptions {
  userId?: string;
  token?: string;
  apiUrl?: string;
  enabled?: boolean;
  lang?: string;
}

export const useDailyRoutines = ({
  userId,
  token,
  apiUrl = '/api',
  enabled = true,
  lang = 'en',
}: UseDailyRoutinesOptions) => {
  const [weeklyRoutines, setWeeklyRoutines] = useState<WeeklyRoutines>({});
  const [categorizedRoutines, setCategorizedRoutines] = useState<{[date: string]: CategorizedTasks}>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Guards against setState after unmount (also stops the background poll loop)
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const fetchRoutines = useCallback(async (quick: boolean) => {
    if (!userId || !token || !enabled) return;

    const url = `${apiUrl}/chat/weekly-routines/${userId}?token=${encodeURIComponent(token)}${quick ? '&quick=true' : ''}&lang=${lang}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch weekly routines: ${response.status}`);
    }

    const data = await response.json();
    if (!data.success) {
      throw new Error(data.error || 'Failed to fetch weekly routines');
    }

    return data;
  }, [userId, token, apiUrl, enabled, lang]);

  const fetchWeeklyRoutines = useCallback(async () => {
    if (!userId || !token || !enabled) return;

    setIsLoading(true);
    setError(null);

    try {
      // Phase 1: Quick fetch - get today fast
      const quickData = await fetchRoutines(true);

      if (quickData && isMountedRef.current) {
        setWeeklyRoutines(quickData.data.routines);
        setCategorizedRoutines(quickData.data.categorized);
        setIsLoading(false);

        // Phase 2: If backend is still generating remaining days, poll until complete
        if (quickData.data.generating) {
          setIsGenerating(true);
          const pollForComplete = async (attempt: number) => {
            if (!isMountedRef.current) return;
            if (attempt > 10) {
              setIsGenerating(false);
              return;
            }

            const delay = attempt < 3 ? 3000 : 5000;
            await new Promise((r) => setTimeout(r, delay));
            if (!isMountedRef.current) return;

            try {
              const fullData = await fetchRoutines(true);
              if (!isMountedRef.current) return;
              if (fullData) {
                const fullDayCount = Object.keys(fullData.data.routines).length;
                const stillGenerating = fullData.data.generating;
                setWeeklyRoutines(fullData.data.routines);
                setCategorizedRoutines(fullData.data.categorized);

                if (fullDayCount < 7 && stillGenerating) {
                  pollForComplete(attempt + 1);
                } else {
                  setIsGenerating(false);
                }
              }
            } catch (err) {
              console.error(`[useDailyRoutines] Poll #${attempt + 1} error:`, err);
              pollForComplete(attempt + 1);
            }
          };

          pollForComplete(0);
        }
      }
    } catch (err: any) {
      console.error('[useDailyRoutines] Error fetching weekly routines:', err);
      if (isMountedRef.current) {
        setError(err.message || 'Failed to fetch weekly routines');
        setIsLoading(false);
      }
    }
  }, [userId, token, enabled, fetchRoutines]);

  const completeTask = useCallback(
    async (date: string, taskId: string) => {
      if (!userId || !token) {
        console.error('[useDailyRoutines] Cannot complete task: missing userId or token');
        return false;
      }

      try {
        const url = `${apiUrl}/chat/routines/${taskId}/complete`;
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            userId,
            date,
          }),
        });

        if (!response.ok) {
          throw new Error(`Failed to complete task: ${response.status}`);
        }

        const data = await response.json();

        if (data.success) {
          if (!isMountedRef.current) return true;

          // Update local state - toggle completed status
          setWeeklyRoutines((prev) => {
            const updated = { ...prev };
            if (updated[date]) {
              updated[date] = updated[date].map((task) =>
                task.id === taskId ? { ...task, completed: !task.completed } : task
              );
            }
            return updated;
          });

          setCategorizedRoutines((prev) => {
            const updated = { ...prev };
            if (updated[date]) {
              const updateCategory = (tasks: DailyTask[]) =>
                tasks.map((task) => (task.id === taskId ? { ...task, completed: !task.completed } : task));

              updated[date] = {
                sabah: updateCategory(updated[date].sabah),
                ogle: updateCategory(updated[date].ogle),
                aksam: updateCategory(updated[date].aksam),
                zamansiz: updateCategory(updated[date].zamansiz),
                all: updateCategory(updated[date].all),
              };
            }
            return updated;
          });

          return true;
        } else {
          throw new Error(data.error || 'Failed to complete task');
        }
      } catch (err: any) {
        console.error('[useDailyRoutines] Error completing task:', err);
        if (isMountedRef.current) {
          setError(err.message || 'Failed to complete task');
        }
        return false;
      }
    },
    [userId, token, apiUrl]
  );

  useEffect(() => {
    if (enabled && userId && token) {
      fetchWeeklyRoutines();
    }
  }, [enabled, userId, token, fetchWeeklyRoutines]);

  return {
    weeklyRoutines,
    categorizedRoutines,
    isLoading,
    isGenerating,
    error,
    refetch: fetchWeeklyRoutines,
    completeTask,
  };
};
